import { useEffect, useState } from "react";
import { CalendarCheck2, CalendarClock, CalendarX2, Download } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ViewingActions from "@/components/viewings/viewing_actions";
import { icsUrl, slotLabel } from "@/components/viewings/viewing_utils";

// Interactive viewing card rendered inside the chat thread for any system
// message that carries viewing_id. The message is only a pointer: the card
// re-reads GET /viewings/{id} on mount, so a stale proposal message can
// never show actionable buttons after the viewing was decided. Several
// messages may reference the same viewing; each card then shows the same
// current state, and a decision made on one card reaches the others (and
// the ViewingPanel strip) through a window event.
export const VIEWING_UPDATED_EVENT = "swiprent:viewing-updated";

export function broadcastViewingUpdate(viewing) {
  if (!viewing?.id) return;
  window.dispatchEvent(new CustomEvent(VIEWING_UPDATED_EVENT, { detail: viewing }));
}

export default function ViewingChatCard({ viewingId, fallbackBody }) {
  const { user } = useAuth();
  const [viewing, setViewing] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get(`/viewings/${viewingId}`)
      .then((res) => alive && setViewing(res.data?.viewing || res.data))
      .catch(() => alive && setFailed(true));
    const onUpdated = (e) => {
      if (alive && e.detail?.id === viewingId) setViewing((v) => ({ ...(v || {}), ...e.detail }));
    };
    window.addEventListener(VIEWING_UPDATED_EVENT, onUpdated);
    return () => {
      alive = false;
      window.removeEventListener(VIEWING_UPDATED_EVENT, onUpdated);
    };
  }, [viewingId]);

  // Loading, demo mode or a viewing we cannot read: fall back to the plain
  // system pill so old style messages still make sense.
  if (!viewing || failed) {
    return (
      <div className="my-3 flex justify-center">
        <span className="rounded-full border border-night-line bg-night-surface px-3.5 py-1.5 text-xs text-night-sub">
          {fallbackBody}
        </span>
      </div>
    );
  }

  function onUpdate(next) {
    setViewing((v) => ({ ...(v || {}), ...next }));
    broadcastViewingUpdate(next);
  }

  const myId = user?.id || user?._id;
  // The API computes proposed_by_me for the requesting user; the id check
  // is the fallback for older payload shapes.
  const iProposed = typeof viewing.proposed_by_me === "boolean"
    ? viewing.proposed_by_me
    : viewing.proposed_by === myId;
  const slots = viewing.slots || [];

  let body;
  if (viewing.status === "confirmed" && viewing.accepted_slot) {
    body = (
      <>
        <p className="flex items-center gap-2 text-sm font-semibold text-night-text">
          <CalendarCheck2 size={15} className="shrink-0 text-brand-primary" />
          Viewing confirmed
        </p>
        <p className="mt-1 text-sm font-medium text-brand-primary">{slotLabel(viewing.accepted_slot)}</p>
        <a
          href={icsUrl(viewing.id)}
          className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-primary-hover"
        >
          <Download size={12} /> Add to calendar
        </a>
      </>
    );
  } else if (viewing.status === "declined") {
    body = (
      <p className="flex items-center gap-2 text-sm font-semibold text-amber-600">
        <CalendarX2 size={15} className="shrink-0" />
        This viewing was rejected. New times can be proposed any time.
      </p>
    );
  } else if (viewing.status === "cancelled") {
    body = (
      <p className="flex items-center gap-2 text-sm font-semibold text-night-sub">
        <CalendarX2 size={15} className="shrink-0" />
        This viewing was cancelled.
      </p>
    );
  } else if (viewing.status === "proposed" && iProposed) {
    body = (
      <>
        <p className="flex items-center gap-2 text-sm font-semibold text-night-text">
          <CalendarClock size={15} className="shrink-0 text-brand-primary" />
          Viewing proposed
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {slots.map((slot) => (
            <span key={slot} className="rounded-full bg-night-tint px-3 py-1.5 text-xs font-medium text-night-sub">
              {slotLabel(slot)}
            </span>
          ))}
        </div>
        <p className="mt-2 text-xs font-medium text-night-sub">Waiting for their reply</p>
      </>
    );
  } else if (viewing.status === "proposed") {
    body = (
      <>
        <p className="flex items-center gap-2 text-sm font-semibold text-night-text">
          <CalendarClock size={15} className="shrink-0 text-brand-primary" />
          Viewing proposed
        </p>
        {slots.length > 0 && (
          <p className="mt-1 text-sm font-medium text-night-text">
            {slots.map((s) => slotLabel(s)).join(" or ")}
          </p>
        )}
        <div className="mt-2.5">
          <ViewingActions viewing={viewing} onUpdate={onUpdate} variant="card" />
        </div>
      </>
    );
  } else {
    // Unknown or legacy status: honest plain text, never fake buttons.
    body = <p className="text-xs text-night-sub">{fallbackBody}</p>;
  }

  return (
    <div className="my-3 flex justify-center">
      <div
        className={`w-full max-w-sm rounded-2xl border px-4 py-3 ${
          viewing.status === "confirmed" ? "border-brand-primary/30 bg-night-tint" : "border-night-line bg-night-surface"
        }`}
      >
        {viewing.listing_title && (
          <p className="mb-1 truncate text-[11px] font-medium uppercase tracking-wide text-night-sub">
            {viewing.listing_title}
          </p>
        )}
        {body}
      </div>
    </div>
  );
}
